/** 첫 문장 분리 */

import { DEV_MODE } from '@/config/dev-mode';

// 사주풀이 내 메인 문장 표시, |는 활용하지 않음
export function splitFirstSentence(text) {
  if (!text || typeof text !== 'string') {
    return { first: '', restSentences: [] };
  }

  const match = text.match(/^(.*?)(?<!\d)\.(?=(\s|\*\*|$|<|\|))(.*)$/s);

  if (match) {
    let first = match[1].trim() + '.';
    let rest = match[3].trim();

    if (rest.startsWith('|')) {
      rest = rest.slice(1).trim();
    }

    const restSentences = splitBySentence(rest);
    return { first, restSentences };
  }

  return { first: text.trim(), restSentences: [] };
}

/** 문장 분리 */
// 요.다.를 기준으로 분리, 개행문자는 활용하지 않음
export function splitBySentence(text) {
  if (!text || typeof text !== 'string') return [];
  const cleaned = text.replace(/(?:\\n|\n)+/g, ' ');
  const regex = /.*?(요|다)[.!?]?(?=\s|$)/g;
  const matches = cleaned.match(regex);
  return (matches || []).map((s) => s.trim());
}

// ** **가 문장 전체를 감쌀 경우
export function splitBySentenceWithAst(text) {
  if (!text || typeof text !== 'string') return [];

  const cleaned = text.replace(/([.!?。！？])(?:\\n|\n)+/g, '$1 ');

  const result = [];
  let remaining = cleaned;

  const emphasisRegex = /\*\*(.+?)\*\*/gs;

  let match;
  let lastIndex = 0;

  while ((match = emphasisRegex.exec(cleaned)) !== null) {
    const start = match.index;
    const end = emphasisRegex.lastIndex;
    const before = cleaned.slice(lastIndex, start);
    const beforeSentences = before.match(/[^.!?。！？\n]+[.!?。！？]/g) || [];
    result.push(...beforeSentences.map((s) => s.trim()));
    result.push(match[0].trim());

    lastIndex = end;
  }
  const after = cleaned.slice(lastIndex);
  const afterSentences = after.match(/[^.!?。！？\n]+[.!?。！？]/g) || [];
  result.push(...afterSentences.map((s) => s.trim()));
  return result.filter(Boolean);
}

/** 데이터 내부 내용 분리 */
// |를 기준으로 내용 분리
export function splitArrayByPipe(sentences) {
  if (!Array.isArray(sentences)) {
    return { before: [], after: [] };
  }

  const before = [];
  const after = [];

  let foundPipe = false;

  for (const sentence of sentences) {
    if (!foundPipe && sentence.includes('|')) {
      const [beforePipe, afterPipe] = sentence.split('|');
      before.push(beforePipe.trim());
      after.push(afterPipe.trim());
      foundPipe = true;
    } else if (!foundPipe) {
      before.push(sentence);
    } else {
      after.push(sentence);
    }
  }

  return { before, after };
}

/** 단순 파이프라인 기준 자르기 */
// |를 기준으로 문장 분리
export function splitSentencesByDivider(text) {
  if (!text) return [];
  return text
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 하이라이트 구분 */
// ** ** 쌍으로 묶인 부분은 다른 스타일 주기 위해 isKeyword 활용
export function splitKeywordParts(text) {
  if (!text) return [];

  const regex = /\*\*(.*?)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, keyword] = match;
    const index = match.index;
    if (index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, index),
        isKeyword: false,
      });
    }
    parts.push({
      text: keyword,
      isKeyword: true,
    });
    lastIndex = index + fullMatch.length;
  }
  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      isKeyword: false,
    });
  }

  return parts;
}

/** ** 제거하기 */
// 단순 ** ** 쌍 제거
export function removeAsterisk(str) {
  let replaced_str = str.replace(/\*\*/g, '');
  return replaced_str;
}

/** 쉼표 기준으로 문자열을 나누어 배열로 변환 */
export function splitCommaSeparatedWords(text) {
  if (!text) return [];
  return text
    .split(',')
    .map((word) => word.trim())
    .filter(Boolean);
}

/** #기준으로 자르고 제거 */
export function parseHashTags(str) {
  if (typeof str !== 'string') return [];

  return str
    .split('#')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 이름 분리 */
// ## ## 쌍 내부 문자는 이름, colored-name 클래스 적용
export function formatDoubleHash(text) {
  if (!text) return '';

  return text.replace(/##(.*?)##/g, `<span class="colored-name">$1</span>`);
}

/**
 * 종합
 * 1. .(마침표) 단위로 문장 분리
 * 2. | 단위로 개행처리
 * 3. ** ** 로 강조 문장 생성
 * 4. ## ## 로 이름 구분
 */
export function transformMarkedText(text, options = {}) {
  if (!text) return '';

  const { keywordClass = 'p-keyword', nameClass = 'colored-name' } = options;

  // 1. **강조문장**
  let sentence = text.replace(/\*\*(.*?)\*\*/g, (_, keyword) => {
    return `<span class="${keywordClass}">${keyword}</span>`;
  });

  // 2. ##이름##
  sentence = sentence.replace(/##(.*?)##/g, (_, name) => {
    return `<span class="${nameClass}">${name}</span>`;
  });

  // 3. | → 줄바꿈
  const html = sentence.replace(/\|/g, '<br />');

  return html;
}

/** 성향분석 제목 특수문자처리 */
export function transformForTitle(text) {
  if (!text) return [];

  return String(text)
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((line) => line.replace(/##\s*([^#]+?)\s*##/g, '$1'));
}
/** 약점 메인 문장 분리 */
export function splitByFirstBoldBlock(text) {
  if (!text || typeof text !== 'string') {
    return { first: '', restSentences: [] };
  }
  const boldMatch = text.match(/\*\*(.+?)\*\*/s);
  if (boldMatch) {
    const matchStart = boldMatch.index;
    const matchEnd = matchStart + boldMatch[0].length;
    const first = boldMatch[0].trim();
    let rest = text.slice(0, matchStart) + text.slice(matchEnd);
    rest = rest.trimStart().startsWith('|') ? rest.trimStart().slice(1).trim() : rest.trim();
    return {
      first,
      restSentences: splitBySentence(rest),
    };
  }
  const fallback = splitBySentence(text);
  return {
    first: fallback[0] || '',
    restSentences: fallback.slice(1),
  };
}

/** 문자열 -> ,기준으로 요소 나누고 배열로 */
export function parseKeywords(text) {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 마지막 단어만 아래로 */
export function insertLineBreak(sentence) {
  const idx = sentence.lastIndexOf(' ');
  if (idx === -1) return sentence;
  return sentence.slice(0, idx) + '<br />' + sentence.slice(idx + 1);
}

/** 격국 분리 */
export function splitGg(text) {
  if (!text) return [];
  if (!text.includes('(')) return [text];
  const txt = text
    .split('(')
    .map((s) => s.trim())
    .filter(Boolean);
  return txt;
}

/**
 * 신년운세 텍스트 변환
 * - ##강조## → <span class="yf-keyword">강조</span>
 * - | → <br />
 * - '. ' → '.<br />'
 * - '! ' → '!<br />'
 *
 * @param {string} text - 변환할 텍스트
 * @param {object} options - 옵션
 * @param {string} options.keywordClass - 강조 클래스명 (기본: 'yf-keyword')
 * @returns {string} HTML 문자열
 */
export function transformYearlyFortuneText(text, options = {}) {
  if (!text) return '';

  const { keywordClass = 'yf-keyword' } = options;

  let result = text;

  // 1. ##강조## → <span class="yf-keyword">강조</span>
  result = result.replace(/##(.*?)##/g, (_, keyword) => {
    return `<span class="${keywordClass}">${keyword}</span>`;
  });

  // 2. | → 줄바꿈
  result = result.replace(/\| ?/g, '<br />');

  // 3. '. ' → '.<br />'
  result = result.replace(/\. /g, '.<br />');

  // 4. '! ' → '!<br />'
  result = result.replace(/! /g, '!<br />');

  return result;
}

// ===== 일운 =====
/**
 * 일운 텍스트 변환
 * - ##강조## → <strong class="bold">강조</strong>
 * - '. ' 또는 '.' → '.<br>'
 * - 중복 <br> 제거
 *
 * @param {string} text - 변환할 텍스트
 * @param {object} options - 옵션
 * @param {string} options.boldClass - 강조 클래스명 (기본: 'bold')
 * @returns {string} HTML 문자열
 */
export function transformDailyFortuneText(text, options = {}) {
  if (!text) return '';

  const { boldClass = 'df-keyword' } = options;

  let result = text;

  // 1. ##강조## → <strong class="bold">강조</strong>
  result = result.replace(/##(.+?)##/g, (_, keyword) => {
    return `<strong class="${boldClass}">${keyword}</strong>`;
  });

  // 2. 마침표 뒤에 <br> 추가
  result = result.replace(/\. /g, '.<br />');

  // 3. 중복 <br> 제거
  result = result.replace(/<br>\s*<br>/g, '<br>');

  return result.trim();
}
